/* ==========================================================================
   Local-currency display

   The store sells in EUR and checkout stays in EUR. Everywhere before
   checkout, prices are shown in the visitor's own currency:

   - Country: the one they picked in the country picker, otherwise the one
     Shopify detects from their location (/browsing_context_suggestions.json),
     otherwise the market Shopify put them in.
   - Rates: EUR-based daily rates from a free public feed, cached for 12 h.
   - Prices rendered in Liquid ("€26,90") and any "€" amount in copy are
     rewritten in place; prices built by the scripts go through
     LUVZ.localization.formatMoney, which this file wraps.
   - The cart shows a line with the EUR amount that will be charged.

   If Shopify itself ever shows a different currency (Shopify Payments with
   local prices turned on), this file steps aside and changes nothing.
   Anything inside [data-money-lock] or a policy page is left in EUR.
   ========================================================================== */

window.LUVZ = window.LUVZ || {};

LUVZ.currency = (function () {
  'use strict';

  var loc = LUVZ.localization;
  var shop = LUVZ.shop || {};
  var BASE = shop.shopCurrency || 'EUR';
  var RATE_URLS = [
    'https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/eur.min.json',
    'https://latest.currency-api.pages.dev/v1/currencies/eur.min.json'
  ];
  var CACHE_KEY = 'luvz_fx_v1';
  var PICK_KEY = 'luvz_country_pick';
  var MAX_AGE = 12 * 60 * 60 * 1000;
  var SKIP = '[data-money-lock], .policy, script, style, textarea, input, select, option, noscript';

  /* ISO country -> currency. Countries not listed fall back to EUR. */
  var MAP = (
    'US:USD,CA:CAD,MX:MXN,GB:GBP,IE:EUR,CH:CHF,LI:CHF,NO:NOK,SE:SEK,DK:DKK,IS:ISK,PL:PLN,CZ:CZK,HU:HUF,RO:RON,BG:BGN,' +
    'HR:EUR,RS:RSD,BA:BAM,MK:MKD,AL:ALL,MD:MDL,UA:UAH,TR:TRY,GE:GEL,AM:AMD,AZ:AZN,KZ:KZT,IL:ILS,AE:AED,SA:SAR,QA:QAR,' +
    'KW:KWD,BH:BHD,OM:OMR,JO:JOD,LB:LBP,EG:EGP,MA:MAD,TN:TND,DZ:DZD,NG:NGN,GH:GHS,KE:KES,UG:UGX,TZ:TZS,ZA:ZAR,' +
    'IN:INR,PK:PKR,BD:BDT,LK:LKR,NP:NPR,CN:CNY,HK:HKD,MO:MOP,TW:TWD,JP:JPY,KR:KRW,SG:SGD,MY:MYR,TH:THB,VN:VND,ID:IDR,' +
    'PH:PHP,AU:AUD,NZ:NZD,FJ:FJD,BR:BRL,AR:ARS,CL:CLP,CO:COP,PE:PEN,UY:UYU,PY:PYG,BO:BOB,EC:USD,SV:USD,PA:USD,PR:USD,' +
    'CR:CRC,GT:GTQ,HN:HNL,NI:NIO,DO:DOP,JM:JMD,TT:TTD,BS:BSD,BB:BBD,BZ:BZD,KY:KYD,BM:BMD,VE:USD,CU:CUP,HT:HTG,' +
    'AF:AFN,IQ:IQD,IR:IRR,UZ:UZS,KG:KGS,TJ:TJS,TM:TMT,MN:MNT,MM:MMK,KH:KHR,LA:LAK,BN:BND,MV:MVR,BT:BTN,ET:ETB,' +
    'RW:RWF,ZM:ZMW,ZW:USD,MZ:MZN,AO:AOA,NA:NAD,BW:BWP,MU:MUR,SC:SCR,MG:MGA,SN:XOF,CI:XOF,ML:XOF,BF:XOF,NE:XOF,TG:XOF,' +
    'BJ:XOF,GW:XOF,CM:XAF,GA:XAF,CG:XAF,CF:XAF,TD:XAF,GQ:XAF,CD:CDF,SD:SDG,LY:LYD,MW:MWK,SL:SLE,LR:LRD,GM:GMD,GN:GNF,' +
    'CV:CVE,MR:MRU,SO:SOS,DJ:DJF,ER:ERN,SS:SSP,BI:BIF,KM:KMF,ST:STN,SZ:SZL,LS:LSL,PG:PGK,SB:SBD,VU:VUV,WS:WST,TO:TOP,' +
    'NC:XPF,PF:XPF,GY:GYD,SR:SRD,AW:AWG,CW:ANG,SX:ANG,AG:XCD,DM:XCD,GD:XCD,KN:XCD,LC:XCD,VC:XCD,AI:XCD,MS:XCD,' +
    'FK:FKP,GI:GIP,SH:SHP,JE:GBP,GG:GBP,IM:GBP,FO:DKK,GL:DKK,BY:BYN,RU:RUB,SY:SYP,YE:YER,PS:ILS,KP:KPW,TL:USD,' +
    'MH:USD,FM:USD,PW:USD,VG:USD,VI:USD,GU:USD,AS:USD,MP:USD,TC:USD,BQ:USD,IO:USD,UM:USD,KI:AUD,NR:AUD,TV:AUD,CK:NZD,' +
    'NU:NZD,TK:NZD,PN:NZD,SJ:NOK,BV:NOK'
  ).split(',').reduce(function (m, pair) { var p = pair.split(':'); m[p[0]] = p[1]; return m; }, {});

  var state = { country: shop.country, code: BASE, rate: 1, ready: false };
  var baseFormat = loc && loc.formatMoney ? loc.formatMoney : null;

  function storage(fn) { try { return fn(window.localStorage); } catch (e) { return null; } }

  /* Shopify already converting? Then leave everything alone. */
  function shopifyConverts() {
    var active = window.Shopify && Shopify.currency && Shopify.currency.active;
    return !!(active && active !== BASE);
  }

  /* ---------- formatting ------------------------------------------------- */

  /* The visitor's language with their country, so a US visitor gets "$77.20"
     rather than "US$77.20" and a German one gets "77,20 $". */
  function localeFor() {
    var nav = (navigator.languages && navigator.languages[0]) || navigator.language || 'en';
    var lang = nav.split('-')[0] || 'en';
    return state.country ? lang + '-' + state.country : nav;
  }

  function fmt(amount, wholeOnly) {
    var opts = { style: 'currency', currency: state.code };
    if (wholeOnly) { opts.minimumFractionDigits = 0; opts.maximumFractionDigits = 0; }
    try {
      return new Intl.NumberFormat(localeFor(), opts).format(amount);
    } catch (e) {
      return state.code + ' ' + amount.toFixed(wholeOnly ? 0 : 2);
    }
  }

  /* EUR cents -> local string */
  function formatLocal(cents) {
    return fmt(Math.round(Number(cents) || 0) / 100 * state.rate, false);
  }

  function active() { return state.ready && state.code !== BASE && state.rate > 0; }

  /* ---------- rewriting "€" text already on the page --------------------- */

  // €1.234,56 · €26,90 · €50 · € 12  (the store formats EUR with a decimal comma)
  var EURO = /€\s?(\d{1,3}(?:[.   ]\d{3})*(?:,\d{1,2})?|\d+(?:,\d{1,2})?)/g;

  function parseEuro(str) {
    var hasCents = /,\d{1,2}$/.test(str);
    var n = parseFloat(str.replace(/[.   ]/g, '').replace(',', '.'));
    return { value: n, whole: !hasCents };
  }

  function convertNode(node) {
    var t = node.nodeValue;
    if (!t || t.indexOf('€') === -1) return;
    var parent = node.parentElement;
    if (!parent || parent.closest(SKIP)) return;
    var next = t.replace(EURO, function (m, num) {
      var p = parseEuro(num);
      if (isNaN(p.value)) return m;
      return fmt(p.value * state.rate, p.whole);
    });
    // only write when something changed: writing fires the observer again
    if (next !== t) node.nodeValue = next;
  }

  function convertTree(root) {
    if (!active() || !root) return;
    if (root.nodeType === 3) { convertNode(root); return; }
    if (root.nodeType !== 1 || (root.closest && root.closest(SKIP))) return;
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
    var n, list = [];
    while ((n = walker.nextNode())) list.push(n);
    list.forEach(convertNode);
    // prices in a few attributes people read (buttons' aria-labels, the add button's label)
    Array.prototype.forEach.call(root.querySelectorAll ? root.querySelectorAll('[aria-label*="€"]') : [], function (el) {
      if (el.closest(SKIP)) return;
      var a = el.getAttribute('aria-label');
      var b = a.replace(EURO, function (m, num) {
        var p = parseEuro(num); return isNaN(p.value) ? m : fmt(p.value * state.rate, p.whole);
      });
      if (b !== a) el.setAttribute('aria-label', b);
    });
  }

  function watch() {
    if (!('MutationObserver' in window)) return;
    new MutationObserver(function (records) {
      if (!active()) return;
      records.forEach(function (r) {
        if (r.type === 'characterData') convertNode(r.target);
        Array.prototype.forEach.call(r.addedNodes || [], convertTree);
      });
    }).observe(document.body, { childList: true, subtree: true, characterData: true });
  }

  /* ---------- cart: say what checkout will charge ------------------------ */

  function paintNotes() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-eur-note]'), function (el) {
      var total = el.getAttribute('data-eur-total');
      if (!active() || total === null) { el.hidden = true; return; }
      var text = (el.getAttribute('data-template') || '[amount]').replace('[amount]', baseFormat ? baseFormat(+total, BASE) : '€' + (total / 100).toFixed(2));
      el.textContent = text;
      el.hidden = !(+total > 0);
    });
  }

  function setNoteTotal(cents) {
    Array.prototype.forEach.call(document.querySelectorAll('[data-eur-note]'), function (el) {
      el.setAttribute('data-eur-total', cents);
    });
    paintNotes();
  }

  /* ---------- picker labels ---------------------------------------------- */

  function paintPicker() {
    Array.prototype.forEach.call(document.querySelectorAll('.loc__current'), function (el) {
      var lang = (el.textContent.split('·')[1] || '').trim();
      el.textContent = state.code + (lang ? ' · ' + lang : '');
    });
    Array.prototype.forEach.call(document.querySelectorAll('[data-set-country]'), function (btn) {
      var code = MAP[btn.getAttribute('data-set-country')] || BASE;
      var cur = btn.querySelector('.loc__cur');
      if (cur) {
        var sym = '';
        try {
          sym = new Intl.NumberFormat(localeFor(), { style: 'currency', currency: code, currencyDisplay: 'narrowSymbol' })
            .formatToParts(0).filter(function (p) { return p.type === 'currency'; })[0].value;
        } catch (e) { /* no symbol */ }
        cur.textContent = code + (sym && sym !== code ? ' ' + sym : '');
      }
      // remember an explicit choice so it beats location detection next time
      btn.addEventListener('click', function () {
        storage(function (s) { s.setItem(PICK_KEY, btn.getAttribute('data-set-country')); });
      });
    });
  }

  /* ---------- country + rates -------------------------------------------- */

  function detectCountry() {
    // ?preview_country=US shows the site as a visitor from that country would see it
    var forced = (location.search.match(/[?&]preview_country=([A-Za-z]{2})/) || [])[1];
    if (forced) return Promise.resolve(forced.toUpperCase());
    var picked = storage(function (s) { return s.getItem(PICK_KEY); });
    if (picked && picked === shop.country) return Promise.resolve(picked);
    return fetch('/browsing_context_suggestions.json', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) {
        var c = j && j.detected_values && j.detected_values.country && j.detected_values.country.handle;
        return c || shop.country;
      })
      .catch(function () { return shop.country; });
  }

  function cachedRates() {
    var c = storage(function (s) { return JSON.parse(s.getItem(CACHE_KEY) || 'null'); });
    return c && c.at && Date.now() - c.at < MAX_AGE ? c.rates : null;
  }

  function fetchRates(i) {
    i = i || 0;
    if (i >= RATE_URLS.length) return Promise.resolve(null);
    return fetch(RATE_URLS[i])
      .then(function (r) { if (!r.ok) throw new Error('fx ' + r.status); return r.json(); })
      .then(function (j) {
        var rates = j && j.eur;
        if (!rates) throw new Error('fx empty');
        storage(function (s) { s.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), rates: rates })); });
        return rates;
      })
      .catch(function () { return fetchRates(i + 1); });
  }

  function apply(country, rates) {
    var code = MAP[country] || BASE;
    var r = rates && rates[code.toLowerCase()];
    state.country = country;
    if (code === BASE || !r) { state.code = BASE; state.rate = 1; state.ready = true; paintNotes(); return; }
    state.code = code; state.rate = r; state.ready = true;
    document.documentElement.setAttribute('data-currency', code);
    convertTree(document.body);
    paintPicker();
    paintNotes();
    document.dispatchEvent(new CustomEvent('luvz:currency', { detail: { code: code, rate: r } }));
  }

  function init() {
    if (shopifyConverts()) return;
    // wrap the shared formatter so script-built prices come out local too
    if (loc && baseFormat) {
      loc.formatMoney = function (cents, code) {
        if (code && code !== BASE) return baseFormat(cents, code);
        return active() ? formatLocal(cents) : baseFormat(cents, code);
      };
      loc.formatBase = baseFormat;
    }
    watch();
    var cached = cachedRates();
    detectCountry().then(function (country) {
      if ((MAP[country] || BASE) === BASE) { apply(country, null); return; }
      if (cached) { apply(country, cached); return; }
      fetchRates().then(function (rates) { apply(country, rates); });
    });
  }

  return {
    init: init,
    setNoteTotal: setNoteTotal,
    get code() { return state.code; },
    get country() { return state.country; },
    isLocal: active
  };
}());

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', LUVZ.currency.init);
else LUVZ.currency.init();
